import { MouseEvent } from 'react'
import { TableBody, TableCell, TableRow, Paper, Box, Chip, Stack, Typography, useTheme } from '@mui/material'
import EnhancedTable from '@common/components/Table/EnhancedTable'
import { Order, HeadCell } from '@common/components/Table/table.types'
import { SPACING } from '@common/theme/spacing'
import QueriesTableToolbar from './QueriesTableToolbar'
import { capitalize } from '@common/utils/stringUtils'
import dayjs from 'dayjs'
import { PaginatedResponse_StudyQueryListSchema_, StudyQueryListSchema } from '@common/config/api/client'

export interface QueriesTableProps {
  page: number
  queries: PaginatedResponse_StudyQueryListSchema_
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onSearch: (value: string) => void
  searchValue?: string
  order: Order
  orderBy: keyof StudyQueryListSchema
  onRequestSort: (event: MouseEvent<unknown>, property: keyof StudyQueryListSchema) => void
  onCreateClick: () => void
  onViewClick: (queryId: string) => void
  hideSubjectColumn?: boolean
}

function EmptyQueriesTable({ searchValue }: { searchValue?: string }) {
  return (
    <Stack spacing={SPACING.minSpacing} alignItems="center" sx={{ py: SPACING.spacing2xl }}>
      <Typography variant="h6">{searchValue ? 'No matching queries found' : 'No queries available'}</Typography>
      <Typography variant="body2" color="text.secondary">
        {searchValue
          ? 'Try adjusting your search criteria or clear the search field'
          : 'Create a new query to get started'}
      </Typography>
    </Stack>
  )
}

export const getStatusChipColor = (status: string) => {
  switch (status) {
    case 'OPEN':
      return 'info-alt'
    case 'GCIS_REVIEW':
      return 'warning-alt'
    case 'RESOLVED':
      return 'success-alt'
    case 'UNRESOLVED':
      return 'error-alt'
    default:
      return 'info-alt'
  }
}

export default function QueriesTable({
  queries,
  onSearch,
  searchValue,
  onRowsPerPageChange,
  onPageChange,
  page,
  order,
  orderBy,
  onRequestSort,
  onCreateClick,
  onViewClick,
  hideSubjectColumn = false,
}: Readonly<QueriesTableProps>) {
  const theme = useTheme()

  const getHeadCells = (hideSubjectColumn?: boolean): readonly HeadCell<StudyQueryListSchema>[] => {
    const baseCells: HeadCell<StudyQueryListSchema>[] = [
      { id: 'query_pseudo_id', label: 'Query ID', disablePadding: false },
    ]

    if (!hideSubjectColumn) {
      baseCells.push({ id: 'subject_pseudo_id', label: 'Subject', disablePadding: false })
    }

    baseCells.push(
      { id: 'site_pseudo_id', label: 'Site ID', disablePadding: false },
      { id: 'timepoint_name', label: 'Timepoint', disablePadding: false },
      { id: 'category', label: 'Category', disablePadding: false },
      { id: 'status', label: 'Status', disablePadding: false },
      { id: 'created_at', label: 'Created', disablePadding: false },
      { id: 'assignees', label: 'Assignees', disablePadding: false },
    )
    return baseCells
  }

  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    if (event.target instanceof HTMLElement && event.target.closest('[data-ignore-row-click="true"]')) {
      return
    }
    onViewClick(id)
  }

  const headCells = getHeadCells(hideSubjectColumn)
  const hasQueries = queries?.items && queries.items.length > 0

  return (
    <Box component="section">
      <QueriesTableToolbar onSearch={onSearch} searchValue={searchValue} onCreateClick={onCreateClick} />
      <Paper
        sx={{
          p: SPACING.paperInnerPadding,
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <EnhancedTable<StudyQueryListSchema>
          rows={queries?.items ?? []}
          headCells={headCells}
          order={order}
          orderBy={orderBy}
          page={page}
          next_cursor={queries?.pagination.next_cursor}
          previous_cursor={queries?.pagination.previous_cursor}
          rowsPerPage={queries?.pagination.limit ?? 50}
          rowsPerPageOptions={[10, 25, 50]}
          onRequestSort={onRequestSort}
          onChangePage={onPageChange}
          onChangeRowsPerPage={onRowsPerPageChange}
        >
          <TableBody>
            {hasQueries ? (
              queries.items.map((row: StudyQueryListSchema) => (
                <TableRow
                  hover
                  onClick={(event) => handleClick(event, row.id)}
                  role="checkbox"
                  tabIndex={-1}
                  key={row.id}
                  sx={{
                    bgcolor: theme.palette.background.default,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <TableCell>{row.query_pseudo_id}</TableCell>
                  {!hideSubjectColumn && <TableCell>{row.subject_pseudo_id}</TableCell>}
                  <TableCell>{row.site_pseudo_id}</TableCell>
                  <TableCell>{row.timepoint_name}</TableCell>
                  <TableCell>{row.category?.includes(':') ? row.category : capitalize(row.category)}</TableCell>
                  <TableCell>
                    <Chip
                      label={capitalize(row.status)}
                      color={getStatusChipColor(row.status)}
                      size="small"
                      sx={{ borderRadius: SPACING.borderRadiusXxl }}
                      data-ignore-row-click="true"
                    />
                  </TableCell>
                  <TableCell>{dayjs(row.created_at).format('DD/MM/YYYY')}</TableCell>
                  <TableCell>{row.assignees?.map((a) => a.name).join(', ')}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={headCells.length + 1}>
                  <EmptyQueriesTable searchValue={searchValue} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </EnhancedTable>
      </Paper>
    </Box>
  )
}
